import { ExternalLink, LayoutDashboard } from "lucide-react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

/** Mirrors `orchestrator/artifacts/dashboard_card.rs` (see v1 KaiDashboardCard). */
export interface KaiDashboardRef {
  id: string;
  name: string;
  description?: string;
  url: string;
  widget_count?: number;
  viewer_role?: string | null;
}

export interface KaiDashboardScopeSummary {
  visibility?: string;
  filters?: string[];
  slicers?: string[];
}

export interface KaiDashboardWidgetSummary {
  title: string;
  entity?: string;
  kpi?: string;
  visualization?: string;
}

export interface KaiDashboardAction {
  label: string;
  url: string;
}

export interface KaiDashboardCardSpec {
  title: string;
  kind: string;
  dashboard: KaiDashboardRef;
  scope_summary?: KaiDashboardScopeSummary;
  widgets?: KaiDashboardWidgetSummary[];
  assumptions?: string[];
  primary_action?: KaiDashboardAction;
  secondary_actions?: KaiDashboardAction[];
}

function isInternal(url: string | undefined): url is string {
  return typeof url === "string" && url.startsWith("/");
}

/**
 * Parse and validate a `dashboard_card` fenced-block body. Returns `null` on any
 * shape mismatch and rejects external URLs so the card only deep-links inside
 * the app. Ported from v1 `parseDashboardCardSpec`.
 */
export function parseDashboardCardSpec(json: string): KaiDashboardCardSpec | null {
  try {
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.title !== "string" || !parsed.title) return null;
    if (typeof parsed.kind !== "string" || !parsed.kind) return null;
    if (!parsed.dashboard || typeof parsed.dashboard !== "object") return null;
    if (typeof parsed.dashboard.id !== "string" || !parsed.dashboard.id) return null;
    if (!isInternal(parsed.dashboard.url)) return null;
    if (parsed.primary_action && !isInternal(parsed.primary_action.url)) return null;
    if (Array.isArray(parsed.secondary_actions)) {
      for (const a of parsed.secondary_actions) {
        if (!isInternal(a?.url)) return null;
      }
    }
    return parsed as KaiDashboardCardSpec;
  } catch {
    return null;
  }
}

function visibilityLabel(spec: KaiDashboardCardSpec): string {
  if (spec.scope_summary?.visibility) return spec.scope_summary.visibility;
  const role = spec.dashboard.viewer_role;
  return role ? `${role} only` : "Organization";
}

/**
 * Renders a `dashboard_card` artifact: the widget dashboard Kai just created,
 * styled like a v2 reports card with an "Open dashboard" deep link and a compact
 * summary of the widgets and applied scope.
 */
export function KaiDashboardCard({ spec }: { spec: KaiDashboardCardSpec }) {
  const { dashboard } = spec;
  const primaryUrl = spec.primary_action?.url ?? dashboard.url;
  const primaryLabel = spec.primary_action?.label ?? "Open dashboard";
  const widgetCount = dashboard.widget_count ?? spec.widgets?.length ?? 0;
  const filters = spec.scope_summary?.filters ?? [];
  const slicers = spec.scope_summary?.slicers ?? [];

  return (
    <div className="glass-panel my-2 flex flex-col gap-3 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary">
            <LayoutDashboard className="size-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{spec.title}</p>
            {dashboard.description && (
              <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                {dashboard.description}
              </p>
            )}
          </div>
        </div>
        <Button size="sm" className="shrink-0 gap-1.5" asChild>
          <Link to={primaryUrl}>
            <ExternalLink className="size-3.5" />
            {primaryLabel}
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="secondary" className="tabular-nums">
          {widgetCount} widgets
        </Badge>
        <Badge variant={dashboard.viewer_role ? "secondary" : "default"}>
          {visibilityLabel(spec)}
        </Badge>
        {filters.map((f, i) => (
          <Badge key={`f-${i}`} variant="urgent">
            {f}
          </Badge>
        ))}
        {slicers.map((s, i) => (
          <Badge key={`s-${i}`} variant="info">
            {s}
          </Badge>
        ))}
      </div>

      {spec.widgets && spec.widgets.length > 0 && (
        <div className="flex flex-col gap-1 border-t border-border/50 pt-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Included widgets
          </span>
          <ul className="flex flex-col gap-1">
            {spec.widgets.map((w, i) => (
              <li key={i} className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate">{w.title}</span>
                {w.visualization && (
                  <Badge variant="outline" className="shrink-0 text-[10px]">
                    {w.visualization}
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {spec.assumptions && spec.assumptions.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {spec.assumptions.join(" · ")}
        </p>
      )}

      {spec.secondary_actions && spec.secondary_actions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {spec.secondary_actions.map((action, i) => (
            <Button key={i} size="sm" variant="outline" asChild>
              <Link to={action.url}>{action.label}</Link>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
